/**
 * useUserPlan — React Query wrapper around billing.getUserPlan().
 *
 * Returns { plan, isLoading, refetch } so components can react to plan
 * changes without knowing anything about Supabase or the billing service.
 *
 * Cache strategy:
 *   - staleTime: 60s   — plan rarely changes; avoids a DB round-trip on
 *                        every render.
 *   - gcTime: 5min     — keep the cached value around across navigations.
 *   - refetchOnWindowFocus: false — plan changes come from explicit actions
 *                                   (checkout), not background tab switches.
 *
 * After markCurrentUserAsPro() succeeds, callers call refetch() so the UI
 * updates without a page reload.
 *
 * Migration note (Phase 2): when the Stripe webhook is live, we may also
 * want to subscribe to a realtime channel or invalidate the cache on
 * session token refresh. For now, explicit refetch is sufficient.
 */

import { useQuery } from "@tanstack/react-query";
import { getUserPlan } from "../lib/api/billing";
import type { Plan } from "../types/billing";

export const USER_PLAN_QUERY_KEY = ["billing", "userPlan"] as const;

export interface UseUserPlanResult {
  plan: Plan;
  isLoading: boolean;
  refetch: () => void;
}

export function useUserPlan(): UseUserPlanResult {
  const { data, isLoading, refetch } = useQuery<Plan>({
    queryKey: USER_PLAN_QUERY_KEY,
    queryFn: getUserPlan,
    // Default to 'free' while loading — no flicker of Pro UI
    placeholderData: "free",
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    plan: data ?? "free",
    isLoading,
    refetch: () => void refetch(),
  };
}
